import * as React from "react";
import * as cornerstone from "cornerstone-core";
import * as cornerstoneTools from "cornerstone-tools";
import cs from "classnames";
import { Button } from "reactstrap";
import CancelableAxiosInstance from "../../api/CancelableAxiosInstance";
import { AiResult, Marker } from "../../api/EvalWorkflowApi";
import { AiPass, AiRegion, ImageRegion, Region, RegionType } from "../../api/StudyApi";
import CornerstoneUtil from "../../util/CornerstoneUtil";
import { isInsideRectangle } from "../../util/RectangleUtility";
import { AuthManager } from "../auth/AuthContext";
import { AiDisplayPreferences } from "./AiDisplayPreferences";
import MarkerMouseTool, { AddingMarkerHandler } from "./MarkerMouseTool";
import { Point } from "./Point";
import RegionMouseTool from "./RegionMouseTool";
import "./viewport.scss";
import ViewportImage from "./ViewportImage";

export const AI_REGION_COLOR = "#ffffff";
const ZOOM_MOUSE_WHEEL_TOOL = "ZoomMouseWheel";
const ZOOM_TOOL = "ZoomTool";
const PAN_TOOL = "Pan";
const WWWC_TOOL = "Wwwc";

CornerstoneUtil.setupTools();
CornerstoneUtil.setupWADOImageLoader();

export enum AnnotationToolMode {
  REGION = "region",
  MARKER = "marker",
}

interface ViewportProps {
  index: number;
  selected: boolean;
  inverted: boolean;
  tool: string;
  imageElement: ViewportImage;
  authManager: AuthManager;

  innerRef?: (element: HTMLDivElement) => void;

  onClickViewport(index: number): void;

  onImageLoaded?(): void;

  onImageTagsChanged(imageId: string, tags: string[]): void;

  displayAiRegions: boolean;
  displayAnnotations: boolean;
  displayCursor: boolean;
  aiDisplayPreferences: AiDisplayPreferences;
  aiRegionMinConfidence: number;
  aiResult?: AiResult;
  displayPasses: boolean;
  imageTags?: string[];
  imageLoaderScheme: string;
  client: CancelableAxiosInstance;
  annotationToolMode?: AnnotationToolMode;

  // Regions related
  saveRegion?(region: Region, index: number): void;

  onRegionIO?(r: Region, isIs: boolean, p: Point): void;

  onRegionHandling?(): void;

  // Markers related
  saveMarker?(marker: Marker, viewportIdx: number): void;

  onMarkerIO?: (marker: Marker, isIs: boolean, p: Point) => void;

  onMarkerHandling?(): void;

  onSelectedRegion?(rectangle: ImageRegion): void;

  onMarkerAdding?: AddingMarkerHandler;
}

interface ViewportState {
  obsMenuStyle?: StyleSheetList;
  className?: string;
  loading: boolean;
}

/*
 * Image viewport
 */
export default class Viewport extends React.Component<ViewportProps, ViewportState> {
  _domElement?: HTMLElement;
  _lastMousePosition?: Point;

  constructor(props: ViewportProps) {
    super(props);

    this.state = {
      loading: false,
    };
  }

  componentWillUnmount() {
    CornerstoneUtil.unregisterLoader(this.props.imageLoaderScheme);
    this.props.client.cancelRequests();
  }

  UNSAFE_componentWillReceiveProps(nextProps: ViewportProps) {
    if (this.props.tool !== nextProps.tool) this.onToolChange(nextProps.tool);

    if (!this.state.loading) {
      if (this.props.displayAnnotations !== nextProps.displayAnnotations) {
        if (nextProps.displayAnnotations) {
          this.enableAnnotationTool(nextProps);
        } else {
          this.disableAnnotationTool(nextProps);
        }
      }
    }

    if (this.props.inverted !== nextProps.inverted) {
      this.setInverted(nextProps.inverted);
    }

    if (this.props.imageElement !== nextProps.imageElement) {
      const nextImageElement = nextProps.imageElement;

      // Different image
      if (this.props.imageElement.path !== nextProps.imageElement.path) {
        this.loadImage(nextImageElement.image.path);
      } else {
        if (this.props.annotationToolMode === AnnotationToolMode.REGION) {
          // Regions may have changed
          const tool = cornerstoneTools.getToolForElement(this._domElement, RegionMouseTool.NAME);
          tool.updateRegions(nextImageElement.regions);
        } else if (this.props.annotationToolMode === AnnotationToolMode.MARKER) {
          const tool = cornerstoneTools.getToolForElement(this._domElement, MarkerMouseTool.NAME);
          if (tool) {
            tool.updateMarkers(nextImageElement.markers);
          }
        }
      }
    }
  }

  componentDidUpdate() {
    cornerstone.resize(this._domElement);
  }

  private setInverted(inverted: boolean) {
    const viewport = cornerstone.getViewport(this._domElement);
    if (viewport) {
      viewport.invert = inverted;
      cornerstone.setViewport(this._domElement, viewport);
    }
  }

  enableAnnotationTool(props: ViewportProps): void {
    if (props.annotationToolMode === AnnotationToolMode.REGION) {
      cornerstoneTools.setToolActiveForElement(this._domElement, RegionMouseTool.NAME, { mouseButtonMask: 2 });
      const tool = cornerstoneTools.getToolForElement(this._domElement, RegionMouseTool.NAME);
      tool.updateRegions(props.imageElement.regions);
    } else if (props.annotationToolMode === AnnotationToolMode.MARKER) {
      const tool = cornerstoneTools.getToolForElement(this._domElement, RegionMouseTool.NAME);
      if (tool) {
        cornerstoneTools.setToolActiveForElement(this._domElement, MarkerMouseTool.NAME, { mouseButtonMask: 2 });
        tool.updateMarkers(props.imageElement.markers);
      }
    }
  }

  disableAnnotationTool(props: ViewportProps) {
    if (props.annotationToolMode === AnnotationToolMode.REGION) {
      cornerstoneTools.setToolDisabled(RegionMouseTool.NAME, {});
    } else if (props.annotationToolMode === AnnotationToolMode.MARKER) {
      cornerstoneTools.setToolDisabled(MarkerMouseTool.NAME, {});
    }
  }

  loadImage(path: string) {
    this.setState({ loading: true }, () => {
      try {
        CornerstoneUtil.loadAndCacheImage(this.props.imageLoaderScheme, path).then((image) => {
          cornerstone.displayImage(this._domElement, image);

          // Reset cornerstone data
          cornerstone.reset(this._domElement);

          this.setInverted(this.props.inverted);

          this.setState({ loading: false });

          if (this.props.onImageLoaded) {
            this.props.onImageLoaded();
          }
        });
      } catch (e) {
        console.error("Can't load image " + path, e);
      }
    });
  }

  set element(elt: any) {
    if (!elt) return;

    if (!this._domElement) {
      this._domElement = elt;
      this._domElement.id = Math.floor((1 + Math.random()) * 0x10000).toString(16);
      this._domElement.addEventListener("cornerstonenewimage", this.onNewImage.bind(this));
      this._domElement.addEventListener("cornerstoneimagerendered", this.onImageRendered.bind(this));
      this._domElement.addEventListener("mousemove", (evt) => this.onMouseMove(evt));

      // Enable cornerstone
      cornerstone.enable(this._domElement);

      this.setInverted(this.props.inverted);

      cornerstoneTools.addToolForElement(this._domElement, cornerstoneTools.ZoomMouseWheelTool, {
        name: ZOOM_MOUSE_WHEEL_TOOL,
      });
      cornerstoneTools.addToolForElement(this._domElement, cornerstoneTools.ZoomTool, { name: ZOOM_TOOL });
      cornerstoneTools.addToolForElement(this._domElement, cornerstoneTools.PanTool, { name: PAN_TOOL });
      cornerstoneTools.addToolForElement(this._domElement, cornerstoneTools.WwwcTool, { name: WWWC_TOOL });

      if (this.props.annotationToolMode === AnnotationToolMode.REGION) {
        cornerstoneTools.addToolForElement(this._domElement, RegionMouseTool);
      } else if (this.props.annotationToolMode === AnnotationToolMode.MARKER) {
        cornerstoneTools.addToolForElement(this._domElement, MarkerMouseTool);
      }

      // Load image
      this.loadImage(this.props.imageElement.image.path);
    } else {
      // Check is the same
      if (this._domElement.id !== elt.id) {
        throw new Error("Can't assign another DOM element");
      }
    }
  }

  onMouseMove(evt: MouseEvent) {
    this._lastMousePosition = { x: evt.offsetX, y: evt.offsetY };
    this.selectMatchingRegion(evt);
    cornerstone.updateImage(this._domElement);
  }

  onNewImage = () => {
    // Always on
    cornerstoneTools.setToolActiveForElement(this._domElement, ZOOM_MOUSE_WHEEL_TOOL, { mouseButtonMask: 1 });
    cornerstoneTools.setToolActiveForElement(this._domElement, WWWC_TOOL, { mouseButtonMask: 4 });

    if (this.props.displayAnnotations) {
      if (this.props.annotationToolMode === AnnotationToolMode.REGION) {
        cornerstoneTools.setToolActiveForElement(this._domElement, RegionMouseTool.NAME, { mouseButtonMask: 2 });

        const tool = cornerstoneTools.getToolForElement(this._domElement, RegionMouseTool.NAME);
        tool.updateRegions(this.props.imageElement.regions);

        if (this.props.saveRegion) {
          tool.registerOnChangeRegion((region: Region) => this.props.saveRegion(region, this.props.index));
        }
        if (this.props.onRegionIO) {
          tool.registerOnOverRegion((region: Region, isIn: boolean, p: Point) =>
            this.props.onRegionIO(region, isIn, p)
          );
        }
        if (this.props.onRegionHandling) {
          tool.registerOnHandling(() => this.props.onRegionHandling());
        }
      } else if (this.props.annotationToolMode === AnnotationToolMode.MARKER) {
        cornerstoneTools.setToolActiveForElement(this._domElement, MarkerMouseTool.NAME, { mouseButtonMask: 2 });

        const tool = cornerstoneTools.getToolForElement(this._domElement, MarkerMouseTool.NAME);
        tool.updateMarkers(this.props.imageElement.markers);

        if (this.props.saveMarker) {
          tool.registerOnChange((marker: Marker) => this.props.saveMarker(marker, this.props.index));
        }
        if (this.props.onMarkerIO) {
          tool.registerOnOver(this.props.onMarkerIO);
        }
        if (this.props.onMarkerHandling) {
          tool.registerOnHandling(() => this.props.onMarkerHandling());
        }
        if (this.props.onMarkerAdding) {
          tool.registerOnAdding(this.props.onMarkerAdding);
        }
      }
    }

    // Tool behavior
    this.onToolChange(this.props.tool);
  };

  onImageRendered = (e: any) => {
    const eventData = e.detail;
    const enabledElement: HTMLElement = eventData.element;
    const context = eventData.canvasContext;

    // Reset transformation and use of pixelToCanvas method in order to keep the font scale
    context.setTransform(1, 0, 0, 1, 0, 0);

    if (this.props.displayAiRegions) {
      this.drawAiRegions(context, enabledElement);
    }

    if (this.props.displayPasses) {
      this.drawRegionPasses(context, enabledElement);
      this.drawMarkerPasses(context, enabledElement);
    }

    if (this.props.displayCursor) {
      this.drawCrossCursor(context);
    }
  };

  drawCrossCursor = (context: CanvasRenderingContext2D) => {
    if (this._lastMousePosition) {
      context.save();

      context.lineWidth = 1;
      context.strokeStyle = "white";

      context.beginPath();

      // move to current position of the mouse
      context.moveTo(this._lastMousePosition.x, 0);
      context.lineTo(this._lastMousePosition.x, this._domElement.clientHeight);

      context.moveTo(0, this._lastMousePosition.y);
      context.lineTo(this._domElement.clientWidth, this._lastMousePosition.y);
      context.stroke();

      context.restore();
    }
  };

  selectMatchingRegion = (e: MouseEvent) => {
    if (this.props.onSelectedRegion && this._domElement) {
      const mouseCoord = cornerstone.canvasToPixel(this._domElement, { x: e.offsetX, y: e.offsetY });

      const regionWrappers = this.buildRegionWrappers();

      const selectedRegion = this.getAndMarkSelectedRegion(regionWrappers, mouseCoord);

      this.props.onSelectedRegion(selectedRegion);
    }
  };

  /**
   * Build a list of object containing a rectangle with _type and imageId associated with the source of this rectangle (aiRegion for type _type AI)
   */
  private buildRegionWrappers() {
    const aiRegions: Array<AiRegion> = this.props.imageElement.aiPasses.reduce(
      (acc, aiPass) => acc.concat(this.filterVisibleAiRegions(aiPass)),
      []
    );

    let regionWrappers: { region: Region; src: any }[] = this.props.imageElement.regions.map((reg) => {
      return { region: reg, src: reg };
    });

    regionWrappers = regionWrappers
      .concat(
        aiRegions.map((aiRegion) => {
          return {
            region: {
              _type: RegionType.AI,
              obsType: aiRegion.type,
              x: aiRegion.x,
              y: aiRegion.y,
              width: aiRegion.width,
              height: aiRegion.height,
              imageId: this.props.imageElement.id,
            },
            src: aiRegion,
          };
        })
      )
      .concat(
        this.props.imageElement.passRegions.map((passRegion) => {
          return {
            region: {
              _type: RegionType.OBSERVATION_GT,
              x: passRegion.x,
              y: passRegion.y,
              width: passRegion.width,
              height: passRegion.height,
              imageId: this.props.imageElement.id,
            },
            src: passRegion,
          };
        })
      );

    return regionWrappers;
  }

  /**
   * Mark _selected attributes of src and
   * Return first selected region (rectangle with type and imageId) inside regionWrappers
   * @param regionWrappers
   * @param mouseCoord
   */
  private getAndMarkSelectedRegion(regionWrappers: { region: Region; src: any }[], mouseCoord: Point): Region {
    let selectedRegion: Region = null;

    regionWrappers.forEach((regWrapper) => {
      if (isInsideRectangle(regWrapper.src, mouseCoord)) {
        if (!selectedRegion) {
          regWrapper.src._selected = true;
          selectedRegion = regWrapper.region;
        } else {
          regWrapper.src._selected = false;
        }
      } else {
        regWrapper.src._selected = false;
      }
    });

    return selectedRegion;
  }

  private drawAiRegions(context: CanvasRenderingContext2D, enabledElement: HTMLElement) {
    if (this.props.imageElement.aiPasses) {
      this.props.imageElement.aiPasses.forEach((aiPass) => {
        this.filterVisibleAiRegions(aiPass).forEach((aiRegion) => {
          context.save();
          context.beginPath();

          this.props.aiDisplayPreferences.applyRegionStyle(context, aiRegion);

          const start = cornerstone.pixelToCanvas(enabledElement, { x: aiRegion.x, y: aiRegion.y });
          const end = cornerstone.pixelToCanvas(enabledElement, {
            x: aiRegion.x + aiRegion.width,
            y: aiRegion.y + aiRegion.height,
          });

          context.rect(start.x, start.y, end.x - start.x, end.y - start.y);
          context.stroke();

          context.beginPath();

          this.props.aiDisplayPreferences.applyAiRegionUpperTextStyle(context);
          context.fillText(this.props.aiDisplayPreferences.computeAiRegionUpperText(aiRegion), start.x, start.y - 3);

          context.beginPath();

          this.props.aiDisplayPreferences.applyAiRegionLowerTextStyle(context, aiPass.type);
          context.fillText(
            this.props.aiDisplayPreferences.computeAiRegionLowerText(aiPass.type),
            start.x + 3,
            end.y - 3
          );

          context.restore();
        });
      });
    }
  }

  private filterVisibleAiRegions(aiPass: AiPass) {
    return aiPass.aiRegions.filter((aiRegion) => aiRegion.confidence >= this.props.aiRegionMinConfidence);
  }

  private drawRegionPasses(context: CanvasRenderingContext2D, enabledElement: HTMLElement) {
    this.props.imageElement.passRegions.forEach((region) => {
      context.save();

      context.beginPath();

      context.strokeStyle = region.badge.color;
      const fill = 8 * (region.passIdx + 0.5);
      const hole = 8 * (region.passIdx + 0.5);

      context.setLineDash([fill, hole]);
      context.globalAlpha = 1;

      const start = cornerstone.pixelToCanvas(enabledElement, { x: region.x, y: region.y });
      const end = cornerstone.pixelToCanvas(enabledElement, {
        x: region.x + region.width,
        y: region.y + region.height,
      });

      context.lineWidth = 3;
      context.rect(start.x, start.y, end.x - start.x, end.y - start.y);
      context.stroke();

      context.beginPath();
      context.globalAlpha = 0.95;
      context.fillStyle = region.badge.color;
      context.textAlign = "right";
      context.font = "12px Arial";
      context.fillText(" " + region.badge.label, end.x + 3, start.y - 3);

      context.restore();
    });
  }

  private drawMarkerPasses(context: CanvasRenderingContext2D, enabledElement: HTMLElement) {
    this.props.imageElement.passMarkers.forEach((marker) => {
      context.save();

      context.beginPath();

      context.strokeStyle = marker._color;
      context.globalAlpha = 1;

      const markerCoord = cornerstone.pixelToCanvas(enabledElement, marker);

      context.lineWidth = 2;

      context.arc(markerCoord.x, markerCoord.y, 4, 0, 2 * Math.PI);

      context.globalAlpha = 0.95;
      context.fillStyle = marker._color;
      context.textAlign = "right";
      context.font = "12px Arial";

      context.fillText("" + marker.passIdx + " : " + marker.obsType, markerCoord.x + 22, markerCoord.y - 6);

      context.stroke();

      context.restore();
    });
  }

  onToolChange(toolName: string) {
    switch (toolName) {
      case "Wwwc":
        cornerstoneTools.setToolActive(WWWC_TOOL, { mouseButtonMask: 1 });
        break;
      case "Zoom":
        cornerstoneTools.setToolActive(ZOOM_TOOL, { mouseButtonMask: 1 });
        break;
      case "Pan":
        cornerstoneTools.setToolActive(PAN_TOOL, { mouseButtonMask: 1 });
        break;
    }
  }

  onClickViewport = () => {
    if (!this.state.loading && this.props.tool !== "adding") {
      this.props.onClickViewport(this.props.index);
    }
  };

  private renderButtonTags(tags: string[]) {
    return tags.map((tag, idx) => {
      const className = cs("tag-button", {
        active: this.imageContainsTag(tag),
        "mr-1": idx !== tags.length - 1,
      });

      return (
        <Button outline size="sm" color="info" key={tag} className={className} onClick={() => this.triggerTag(tag)}>
          {tag}
        </Button>
      );
    });
  }

  private imageContainsTag(tag: string): boolean {
    return this.props.imageElement.image.tags.includes(tag);
  }

  triggerTag = (tag: string) => {
    const { tags: initialTags } = this.props.imageElement.image;

    const tags = this.imageContainsTag(tag) ? initialTags.filter((t) => t !== tag) : initialTags.concat(tag);

    this.props.onImageTagsChanged(this.props.imageElement.id, tags);
  };

  displayAiResult() {
    if (this.props.aiDisplayPreferences.displayStudyResult) {
      return (
        <div className="vp-top-left">
          <div className="small">
            <div>BoneView</div>
            {this.props.displayAiRegions && (
              <div>{this.props.aiDisplayPreferences.displayResult(this.props.aiResult)}</div>
            )}
          </div>
        </div>
      );
    }
  }

  render() {
    return (
      <div
        className={cs("viewport over-1", { selected: this.props.selected })}
        style={this.state.loading ? { opacity: 0.2 } : {}}
        ref={(elt) => {
          this.props.innerRef(elt);
          this.element = elt;
        }}
        onClick={this.onClickViewport}
        onContextMenu={(evt) => evt.preventDefault()}
      >
        {this.displayAiResult()}

        {this.props.imageTags && <div className="vp-top-right">{this.renderButtonTags(this.props.imageTags)}</div>}
      </div>
    );
  }
}
